import { Briefcase, Landmark, UserRound, type LucideIcon } from 'lucide-react';
import { env } from './env';

/**
 * The applications of the platform, exactly as identity-service names them
 * (`APPLICATIONS` in `packages/common/src/authorization/application-access.ts`).
 *
 * Application access answers only "may this account open this application at
 * all?". It is data on the account (`user_application_access`), returned by
 * `/auth/me` as `applications`. Nothing here decides it — the backend does,
 * and refuses every request of an application the account does not hold.
 */
export const APPLICATIONS = {
  HRMS: 'HRMS',
  FINANCE: 'FINANCE',
  EMPLOYEE_PORTAL: 'EMPLOYEE_PORTAL',
} as const;

export type ApplicationCode = (typeof APPLICATIONS)[keyof typeof APPLICATIONS];

export const APPLICATION_CODES: readonly ApplicationCode[] = Object.values(APPLICATIONS);

export function isApplicationCode(value: unknown): value is ApplicationCode {
  return typeof value === 'string' && (APPLICATION_CODES as readonly string[]).includes(value);
}

export interface ApplicationDefinition {
  code: ApplicationCode;
  name: string;
  description: string;
  icon: LucideIcon;
  /** Base URL the application is served from, when configured. */
  url: string | undefined;
}

export const APPLICATION_DEFINITIONS: Record<ApplicationCode, ApplicationDefinition> = {
  HRMS: {
    code: 'HRMS',
    name: 'HRMS',
    description: 'People, attendance, leave, projects and reports',
    icon: Briefcase,
    url: env.appUrls.HRMS,
  },
  FINANCE: {
    code: 'FINANCE',
    name: 'Finance',
    description: 'Companies, vendors, expenses, assets, invoices and access',
    icon: Landmark,
    url: env.appUrls.FINANCE,
  },
  EMPLOYEE_PORTAL: {
    code: 'EMPLOYEE_PORTAL',
    name: 'Employee Portal',
    description: 'My profile, attendance, leave and documents',
    icon: UserRound,
    url: env.appUrls.EMPLOYEE_PORTAL,
  },
};

/**
 * The applications the UI offers to switch to — the application switcher, the
 * "no access" page and cross-application links — in menu order.
 *
 * The Employee Portal is deliberately left out for now: nothing links to,
 * redirects to or launches it. The portal application, its routes and
 * portal-service are unchanged, and the backend still decides who may use it;
 * this list only decides what the other applications offer. Add
 * `APPLICATIONS.EMPLOYEE_PORTAL` here to offer it again.
 */
export const SWITCHABLE_APPLICATION_CODES: readonly ApplicationCode[] = [
  APPLICATIONS.HRMS,
  APPLICATIONS.FINANCE,
];

export function isSwitchableApplication(code: ApplicationCode): boolean {
  return SWITCHABLE_APPLICATION_CODES.includes(code);
}

/**
 * The applications this account may switch to: exactly the switchable ones it
 * holds, in menu order. `held` is `/auth/me` → `applications` — the same
 * `user_application_access` data the backend's ApplicationAccessGuard enforces
 * on every request — so visibility is never decided by a role, an email or the
 * Owner flag.
 */
export function switchableApplications(held: readonly ApplicationCode[]): ApplicationDefinition[] {
  return SWITCHABLE_APPLICATION_CODES.filter((code) => held.includes(code)).map(
    (code) => APPLICATION_DEFINITIONS[code],
  );
}

/** Backend message for a refused application (ApplicationAccessGuard). */
export const NO_APPLICATION_ACCESS_MESSAGE = 'Your account does not have access to this application';
